import { useEffect, useRef, type ReactNode } from "react";
import { describeError } from "../api/http";
import { useAppState, useRecommendLineup, useSync } from "../api/queries";
import type { LineupSlot, SyncRun } from "../api/types";
import { Page } from "../components/layout/Page";
import { Pitch, type PitchPlayer } from "../components/Pitch";
import { Scoreline, Stat } from "../components/Scoreline";
import { Button, ButtonLink } from "../components/ui/Button";
import { Banner, EmptyState, ErrorState } from "../components/ui/ErrorState";
import { Skeleton } from "../components/ui/Skeleton";
import { count, desdeHace, fechaCorta, money, points } from "../lib/format";

/**
 * Pantalla *Inicio* — M8-T01 (UC-08.01).
 *
 * EL CAMPO ABRE LA APLICACIÓN
 *   La sección 8.4 hace del campo de juego el elemento firma, y aquí entra **a sangre**: pegado a
 *   la cabecera, sin márgenes, con el once de la próxima jornada y el rótulo de televisión encima.
 *   Debajo, sin un pixel de separación, la franja de marcador. Esas dos piezas son la pantalla;
 *   todo lo demás vive en el cuerpo, con su ritmo normal.
 *
 * DOS FUENTES QUE NO SE PISAN
 *   Las **cifras** salen de `GET /api/state` (`useAppState`), que no se sondea. El **estado vivo**
 *   de la sincronización sale de `useSync()`, que sí sondea `GET /api/sync/runs` cada dos segundos
 *   mientras haya tanda. Mirar `state.running` para el progreso daría una foto de hace medio
 *   minuto; mirar `useSync().running` da la de ahora. Cuando la tanda termina, `useSync` invalida
 *   la caché entera y las cifras se refrescan **sin recargar la página** (aceptación de la tarea).
 *
 * POR QUÉ EL ONCE SE PIDE EN UN EFECTO
 *   `POST /api/recommend/lineup` es una petición con cuerpo, así que la capa de datos la expone
 *   como mutación (`useRecommendLineup`) y no como consulta. Se lanza al montar y **solo** vuelve
 *   a lanzarse cuando cambia `syncedAt`: es decir, cuando ha entrado plantilla nueva. Sin ese
 *   testigo, cada refresco del estado dispararía otro cálculo del once.
 *
 * QUÉ NO PINTA ESTA PANTALLA
 *   La «decisión de la jornada» del mockup —el mejor movimiento con su comparador— necesita
 *   `POST /api/recommend/transfers` y, sobre todo, las métricas de quien sale y quien entra, que
 *   `Move` no trae (`PlayerRef` es id, nombre, posición, equipo y precio). La descripción de la
 *   tarea no la pide y su aceptación tampoco: el acceso a *Recomendar* ocupa su sitio.
 */

/* ============================================================ vocabulario de la tanda ==== */

/**
 * Los pasos de una tanda completa, en el orden en que los ejecuta el servidor. Se enumeran aquí
 * para poder enseñar los cinco desde el primer segundo —los que aún no han corrido, como
 * *pendiente*— en vez de ir apareciendo de uno en uno.
 */
const PASOS_DE_LA_TANDA = [
  "catalog",
  "rounds",
  "me",
  "market",
  "players",
] as const;

/** Nombre de cada tipo de sincronización, en español y sin jerga (sección 8.3). */
const NOMBRE_DEL_TIPO: Record<string, string> = {
  all: "todo",
  catalog: "catálogo",
  rounds: "jornadas",
  me: "mi estado",
  market: "mercado",
  players: "fichas",
};

function nombreDelTipo(kind: string): string {
  return NOMBRE_DEL_TIPO[kind] ?? kind;
}

/** El mismo nombre para encabezar una fila: *Catálogo*, *Mi estado*, *Fichas*. */
function tituloDelTipo(kind: string): string {
  const nombre = nombreDelTipo(kind);
  return nombre.charAt(0).toUpperCase() + nombre.slice(1);
}

/**
 * Qué recuento resume cada paso. Los nombres son los que escribe cada job en su fila de
 * `sync_runs`, sin prefijo: la fila de la tanda (`all`) no lleva recuentos hasta el final.
 */
const RECUENTO: Record<string, { clave: string; unidad: string }> = {
  catalog: { clave: "players", unidad: "jugadores" },
  rounds: { clave: "fixtures", unidad: "partidos" },
  me: { clave: "squad", unidad: "en plantilla" },
  market: { clave: "listings", unidad: "ventas" },
  players: { clave: "players", unidad: "fichas" },
};

/** Lo que se enseña a la derecha de cada paso del progreso. */
export function resumenDelPaso(kind: string, run: SyncRun | null): string {
  if (run === null) return "pendiente";
  if (run.status === "running") return "en curso";
  if (run.status === "error") return "ha fallado";

  const medida = RECUENTO[kind];
  const valor = medida === undefined ? undefined : run.stats[medida.clave];
  if (medida === undefined || valor === undefined) return "hecho";

  // Las fichas se piden una a una con espera de cortesía: lo interesante es cuántas van del total.
  const pedidas = kind === "players" ? run.stats["requested"] : undefined;
  return pedidas === undefined
    ? `${count(valor)} ${medida.unidad}`
    : `${count(valor)} / ${count(pedidas)} ${medida.unidad}`;
}

/**
 * La fila que abre la tanda viva: en una sincronización completa es la de `all`, que arranca
 * antes que sus pasos y por tanto tiene el identificador más bajo de todas las que están en
 * marcha. Si se lanzó un tipo suelto, esa misma fila es el único paso.
 */
export function raizDeLaTanda(runs: SyncRun[]): SyncRun | null {
  const vivas = runs.filter((run) => run.status === "running");
  if (vivas.length === 0) return null;
  return vivas.reduce((una, otra) => (una.id <= otra.id ? una : otra));
}

/** Los pasos de la tanda viva, con la fila que los cuenta cuando ya han corrido. */
export function pasosDeLaTanda(
  runs: SyncRun[],
  raiz: SyncRun,
): { kind: string; run: SyncRun | null }[] {
  if (raiz.kind !== "all") return [{ kind: raiz.kind, run: raiz }];

  // Los pasos de una tanda son las filas posteriores a la suya: nacen dentro de ella.
  const dentro = runs.filter((run) => run.id > raiz.id);
  return PASOS_DE_LA_TANDA.map((kind) => ({
    kind,
    run: dentro.find((run) => run.kind === kind) ?? null,
  }));
}

/* =========================================================================== textos ===== */

/** Lo que caben de un mensaje del servidor en una línea de la interfaz. */
const MAXIMO_DEL_MENSAJE = 140;

/**
 * Un mensaje de `sync_runs` puede ser un texto largo de varias líneas —el candado del modo sin
 * red trae un párrafo entero—. En pantalla cabe la primera línea; el resto sigue disponible en el
 * `title` para quien pase por encima.
 */
export function recortar(texto: string, maximo = MAXIMO_DEL_MENSAJE): string {
  const primera =
    texto
      .split("\n")
      .map((linea) => linea.trim())
      .find((linea) => linea !== "") ?? "";
  if (primera.length <= maximo) return primera;
  return `${primera.slice(0, maximo - 1).trimEnd()}…`;
}

/* ============================================================================ piezas ===== */

/** Cuánto suma el once: es la cifra del rótulo sobre el campo. */
function puntosDelOnce(starters: LineupSlot[]): number {
  return starters.reduce((total, slot) => total + slot.expected, 0);
}

function aFichas(starters: LineupSlot[]): PitchPlayer[] {
  return starters.map((slot) => ({
    id: slot.player.id,
    name: slot.player.name,
    position: slot.player.position,
    expected: slot.expected,
    // Los cuatro estados de la sección 8.4; aquí solo cabe el aviso, que además va escrito.
    state: slot.warning === undefined ? "normal" : "warn",
    warning: slot.warning ?? null,
  }));
}

/** El hueco que ocupará el campo mientras llega, con su proporción exacta (sección 8.2). */
function EsqueletoDeCabecera() {
  return (
    <>
      <Skeleton
        variant="pitch"
        className="[&>*]:rounded-none"
        label="Cargando el once de la próxima jornada"
      />
      <div className="grid grid-cols-3 border-t-[3px] border-pitch bg-surface">
        {["presupuesto", "plantilla", "puntos"].map((celda) => (
          <div
            key={celda}
            className="border-r border-border px-3 py-2.5 last:border-r-0"
          >
            <Skeleton variant="text" className="w-3/4" label="Cargando" />
            <div className="mt-1.5 h-[26px] animate-pulse rounded-md bg-white/[0.07]" />
          </div>
        ))}
      </div>
    </>
  );
}

/** Un aviso o un vacío que ocupa el sitio del campo, con el mismo borde inferior. */
function EnLugarDelCampo({ children }: { children: ReactNode }) {
  return <div className="border-b border-border px-3.5 py-5">{children}</div>;
}

interface TarjetaProps {
  titulo: string;
  children: ReactNode;
}

function Tarjeta({ titulo, children }: TarjetaProps) {
  return (
    <section className="grid gap-3 rounded-xl border border-border bg-surface p-3">
      <h2 className="text-[15px] leading-none text-ink">{titulo}</h2>
      {children}
    </section>
  );
}

/* ========================================================================== pantalla ===== */

export function Home() {
  const estado = useAppState();
  const { sync, isStarting, startError, running, isRunning, runs } = useSync();
  const once = useRecommendLineup();

  const { mutate: pedirOnce, reset: olvidarOnce } = once;
  const pedido = useRef<string | null>(null);

  /**
   * El once se pide una vez por «foto» de los datos: al montar y cada vez que una sincronización
   * deja un `syncedAt` nuevo. Mientras el estado no haya llegado no se pide nada, porque no se
   * sabría contra qué foto se está calculando.
   */
  const foto = estado.isPending ? null : String(estado.data?.syncedAt ?? null);

  useEffect(() => {
    if (foto === null || pedido.current === foto) return;
    pedido.current = foto;
    olvidarOnce();
    pedirOnce({ plan: false });
  }, [foto, pedirOnce, olvidarOnce]);

  function reintentarOnce(): void {
    olvidarOnce();
    pedirOnce({ plan: false });
  }

  const datos = estado.data;
  const sinSincronizar = datos !== undefined && datos.syncedAt === null;
  const alineacion = once.data;
  const calculandoOnce = once.isPending || once.isIdle;

  /* ---------------------------------------------------------------- la cabecera a sangre - */

  let cabecera: ReactNode;
  if (estado.isPending) {
    cabecera = <EsqueletoDeCabecera />;
  } else if (datos === undefined) {
    cabecera = null;
  } else {
    let campo: ReactNode;
    if (!datos.biwengerConfigured) {
      /*
       * Va **antes** que el token caducado porque es más de fondo: sin conexión no es que el token
       * se haya estropeado, es que nunca lo hubo. Es lo primero que ve una instalación recién
       * puesta, y el aviso lleva a donde se arregla en vez de mandar a leer un fichero.
       */
      campo = (
        <div className="px-3.5 py-3.5 md:px-[18px]">
          <Banner tone="warning" title="Falta conectar con Biwenger">
            Sin el token, la liga, el usuario y la versión no se pueden traer
            tus datos. Se copian del navegador con Biwenger abierto y se pegan
            en Ajustes.
            {/* `Banner` mete sus hijos en un `span`, así que el botón va dentro de otro que sí
                puede ocupar su línea: un `p` ahí dentro no sería HTML válido. */}
            <span className="mt-2.5 block">
              <ButtonLink to="/ajustes" size="sm">
                Conectar con Biwenger
              </ButtonLink>
            </span>
          </Banner>
        </div>
      );
    } else if (datos.authError) {
      // Con el token caducado nada de lo que se vea va a poder actualizarse: el campo cede el
      // sitio y lo primero de la pantalla es el aviso y sus instrucciones.
      campo = (
        <div className="px-3.5 py-3.5 md:px-[18px]">
          <Banner tone="error" title="El token de Biwenger ha caducado">
            Las sincronizaciones seguirán fallando hasta que lo renueves:
            cópialo otra vez del navegador y pégalo en Ajustes.
            <span className="mt-2.5 block">
              <ButtonLink to="/ajustes" size="sm">
                Renovar el token
              </ButtonLink>
            </span>
          </Banner>
        </div>
      );
    } else if (sinSincronizar) {
      campo = (
        <EnLugarDelCampo>
          <EmptyState
            title="Aún no hay datos"
            message="Pulsa Sincronizar para traer tu plantilla, el mercado y las métricas."
          />
        </EnLugarDelCampo>
      );
    } else if (calculandoOnce) {
      campo = (
        <Skeleton
          variant="pitch"
          className="[&>*]:rounded-none"
          label="Cargando el once de la próxima jornada"
        />
      );
    } else if (alineacion === undefined) {
      campo = (
        <EnLugarDelCampo>
          <ErrorState
            title="No se ha podido calcular el once"
            message={describeError(once.error)}
            onRetry={reintentarOnce}
          />
        </EnLugarDelCampo>
      );
    } else if (alineacion.starters.length === 0) {
      campo = (
        <EnLugarDelCampo>
          <EmptyState
            title="Todavía no hay once"
            message="Sincroniza para traer tu plantilla y calcular la alineación de la próxima jornada."
          />
        </EnLugarDelCampo>
      );
    } else {
      const jornada =
        alineacion.roundNumber === null
          ? "Próxima jornada"
          : `Jornada ${String(alineacion.roundNumber)}`;
      campo = (
        <Pitch
          bleed
          formation={alineacion.formation}
          players={aFichas(alineacion.starters)}
          bug={
            <Stat
              label={`${jornada} · ${alineacion.formation}`}
              value={points(puntosDelOnce(alineacion.starters))}
              caption="Puntos esperados"
              size={40}
            />
          }
        />
      );
    }

    cabecera = (
      <>
        {campo}
        <Scoreline
          cells={[
            { label: "Presupuesto", value: money(datos.budget), tone: "money" },
            { label: "Plantilla", value: money(datos.squadValue) },
            { label: "Puntos", value: count(datos.pointsTotal) },
          ]}
        />
      </>
    );
  }

  /* ------------------------------------------------------------------- la sincronización - */

  const raiz = raizDeLaTanda(runs);
  const ultima = runs.find((run) => run.status !== "running") ?? null;
  const falloReciente =
    !isRunning && ultima !== null && ultima.status === "error" ? ultima : null;

  const enMarcha = isRunning || isStarting;
  const hayEstado = datos !== undefined;
  const etiquetaDelBoton = isRunning
    ? `Sincronizando… ${nombreDelTipo(running?.kind ?? "all")}`
    : isStarting
      ? "Sincronizando…"
      : "Sincronizar";

  return (
    <Page
      title="Inicio"
      action={
        <ButtonLink to="/ajustes" size="sm">
          Ajustes
        </ButtonLink>
      }
      bleed={cabecera}
    >
      {estado.isError ? (
        <ErrorState
          message={describeError(estado.error)}
          onRetry={() => void estado.refetch()}
        />
      ) : null}

      <Button
        variant="primary"
        block
        onClick={() => sync()}
        disabled={enMarcha}
        aria-describedby={
          hayEstado ? "inicio-ultima-sincronizacion" : undefined
        }
      >
        {etiquetaDelBoton}
      </Button>

      {datos !== undefined ? (
        <p
          id="inicio-ultima-sincronizacion"
          className="-mt-1 text-center text-xs text-muted"
        >
          {datos.syncedAt === null ? (
            "Todavía no se ha sincronizado."
          ) : (
            <>
              Sincronizado {desdeHace(datos.syncedAt)} ·{" "}
              <time dateTime={datos.syncedAt}>
                {fechaCorta(datos.syncedAt)}
              </time>
            </>
          )}
        </p>
      ) : null}

      {startError !== null ? (
        <p role="alert" className="text-center text-xs text-money">
          {startError.code === "sync_running"
            ? "Ya hay una sincronización en marcha."
            : describeError(startError)}
        </p>
      ) : null}

      {isRunning && raiz !== null ? (
        <section
          role="status"
          aria-live="polite"
          aria-label="Progreso de la sincronización"
          className="grid gap-3 rounded-xl border border-border bg-surface p-3"
        >
          <h2 className="text-[15px] leading-none text-ink">Progreso</h2>
          <ul className="grid gap-[7px] text-[13px]">
            {pasosDeLaTanda(runs, raiz).map((paso) => (
              <li key={paso.kind} className="flex justify-between gap-3">
                <span>{tituloDelTipo(paso.kind)}</span>
                <span
                  className={
                    paso.run === null
                      ? "font-mono tabular-nums text-muted"
                      : "font-mono tabular-nums text-ink"
                  }
                >
                  {resumenDelPaso(paso.kind, paso.run)}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted">
            La primera sincronización de fichas puede tardar varios minutos: se
            pide una por segundo.
          </p>
        </section>
      ) : null}

      {falloReciente !== null ? (
        <Banner
          tone="error"
          title={`La última sincronización de ${nombreDelTipo(falloReciente.kind)} ha fallado`}
        >
          <span title={falloReciente.message ?? undefined}>
            {falloReciente.message === null
              ? "No se ha guardado el motivo."
              : recortar(falloReciente.message)}
          </span>
        </Banner>
      ) : null}

      {datos !== undefined ? (
        <Tarjeta titulo="Mi plan">
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Compras" value={count(datos.plan.buys)} size={26} />
            <Stat label="Ventas" value={count(datos.plan.sells)} size={26} />
            <Stat
              label="Neto"
              value={money(datos.plan.netBudget)}
              tone={datos.plan.netBudget < 0 ? "down" : "money"}
              size={26}
            />
          </div>
          <p className="text-xs text-muted">
            {datos.plan.buys === 0 && datos.plan.sells === 0
              ? "El plan está vacío. Añade compras desde Mercado y ventas desde Plantilla."
              : datos.plan.valid
                ? "Plan válido."
                : "El plan no cumple alguna regla de la liga. Revísalo en Plantilla."}
          </p>
          <ButtonLink to="/plantilla" size="sm" block>
            Ver en Plantilla
          </ButtonLink>
        </Tarjeta>
      ) : null}

      <div className="grid grid-cols-2 gap-2">
        <ButtonLink to="/recomendar" block>
          Recomendar
        </ButtonLink>
        <ButtonLink to="/alineacion" block>
          Alineación
        </ButtonLink>
      </div>
    </Page>
  );
}
